import * as React from "react";
import { observer } from "mobx-react";

import { UitkLink } from "uitk-react-link";
import { AnalyticsConfig } from "uitk-react-utils";
import { UitkCard, UitkCardContent, UitkCardTextSegment } from "uitk-react-cards";

import JumplinksTitle from "./components/JumplinksTitle";
import TravelGuideLink from "./components/TravelGuideLink";
import { getJumplinkLocalizedNames } from "./JumplinksUtils";

import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { Action, FlexTrackingInfo, sendDelayedTrackEvent } from "components/utility/analytics/FlexAnalyticsUtils";

import { JumplinksProps, JumplinkItem } from "./typings";
import { withStores } from "stores";

const Jumplinks = withStores(
  "analytics",
  "dx"
)(
  observer((props: JumplinksProps) => {
    const { flexComponent, context, dx } = props;
    const { model } = flexComponent;
    const { jumplinksArray } = dx;

    const keyHelper = new ItemKeyHelper("jumplinks");

    // Main logic in the following is covered in test, only branch coverage is lacking
    /* istanbul ignore next */
    React.useEffect(() => {
      const scrollOnPageLoad = (): void => {
        const jumplinkPathName = location.hash.replace("#", "");

        const associatedJumplink = jumplinksArray.find(
          (jumplink: JumplinkItem) => jumplink.pathName === jumplinkPathName
        );

        if (!associatedJumplink) {
          return;
        }

        const element = document.getElementById(associatedJumplink.id);
        if (!element) {
          return;
        }

        scrollTo(element);
      };

      window.addEventListener("load", scrollOnPageLoad);

      return () => window.removeEventListener("load", scrollOnPageLoad);
    });

    const handleClick = (id: string) => (): void => {
      const element = document.getElementById(id);
      if (!element) {
        return;
      }

      scrollTo(element);
    };

    /* istanbul ignore next */
    const scrollTo = (elementToScrollTo: HTMLElement): void => {
      let wizardHeight: number = 0;

      const { deviceInformation } = context;
      const isMobile = deviceInformation?.mobile;

      if (!isMobile) {
        const wizardElement = document.getElementById("wizard-hotel-pwa-1");

        if (wizardElement) {
          wizardHeight = wizardElement.offsetHeight;
        }
      }

      const offsetPosition = elementToScrollTo.offsetTop;

      if (offsetPosition) {
        window.scrollTo({
          top: offsetPosition - wizardHeight,
          behavior: "smooth"
        });
      } else {
        elementToScrollTo.scrollIntoView();
      }
      getFirstNavElement(elementToScrollTo).focus();
    };

    /* istanbul ignore next */
    const getFirstNavElement = (parentElement: HTMLElement): HTMLElement => {
      if (parentElement.getElementsByTagName("a").length) {
        return parentElement.getElementsByTagName("a")[0];
      }

      if (parentElement.getElementsByTagName("button").length) {
        return parentElement.getElementsByTagName("button")[0];
      }

      return parentElement;
    };

    const getAnalytics = (jumplinkName: string): AnalyticsConfig => ({
      id: `TG.LP.Dest.JumpTo.jumplinks.${jumplinkName}`,
      callback: (id: string, description: string) => {
        const flexTrackingInfo: FlexTrackingInfo = {
          moduleName: id,
          action: Action.CLICK,
          linkName: description
        };
        sendDelayedTrackEvent(flexTrackingInfo, props.analytics);
      }
    });

    return (
      <div id={flexComponent.id} className="uitk-cell all-cell-1-1 Jumplinks" data-testid="jumplinks">
        <UitkCard className="uitk-flat-border-top">
          <UitkCardContent>
            <UitkCardTextSegment>
              <JumplinksTitle model={model} context={context} />

              {jumplinksArray.map((jumplink: JumplinkItem) => (
                <UitkLink
                  key={keyHelper.next()}
                  onClick={handleClick(jumplink.id)}
                  analytics={getAnalytics(jumplink.pathName)}
                  aria-label={jumplink.pathName}
                >
                  <a tabIndex={0} href={`#${jumplink.pathName}`}>
                    {getJumplinkLocalizedNames(jumplink)}
                  </a>
                </UitkLink>
              ))}
            </UitkCardTextSegment>

            <TravelGuideLink model={model} context={context} />
          </UitkCardContent>
        </UitkCard>
      </div>
    );
  })
);

export default Jumplinks;
